import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { existing: String }

  connect() {
    this.setupMultiTagInput()
  }

  setupMultiTagInput() {
    const input = this.element
    const existingTags = this.existingValue ? this.existingValue.split(',') : []
    
    // Skryjeme původní input
    input.style.display = 'none'
    
    // Vytvoříme container pro multi-tag input
    const container = document.createElement('div')
    container.className = 'multi-tag-container'
    container.style.cssText = `
      border: 1px solid #dee2e6;
      border-radius: 0.375rem;
      padding: 0.375rem 0.75rem;
      min-height: 38px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem;
      background-color: white;
      cursor: text;
    `
    
    // Vytvoříme input pro nové tagy
    const tagInput = document.createElement('input')
    tagInput.type = 'text'
    tagInput.placeholder = 'Zadejte kategorii...'
    tagInput.className = 'tag-input'
    tagInput.style.cssText = `
      border: none;
      outline: none;
      flex: 1;
      min-width: 120px;
      font-size: 1rem;
    `
    
    container.appendChild(tagInput)
    input.parentNode.insertBefore(container, input)
    
    // Načteme existující tagy
    this.loadExistingTags(container, tagInput, input)
    
    // Nastavíme event listenery
    this.setupTagInputEvents(container, tagInput, input, existingTags)
  }

  loadExistingTags(container, tagInput, hiddenInput) {
    const currentTags = hiddenInput.value ? hiddenInput.value.split(',').map(t => t.trim()).filter(t => t) : []
    
    currentTags.forEach(tag => {
      this.addTag(container, tagInput, hiddenInput, tag)
    })
    
    // Pokud nejsou žádné tagy, zobrazíme placeholder
    if (currentTags.length === 0) {
      tagInput.placeholder = 'Zadejte kategorii...'
    }
  }

  addTag(container, tagInput, hiddenInput, tagText) {
    const tag = document.createElement('span')
    tag.className = 'tag'
    tag.style.cssText = `
      background-color: #e3f2fd;
      color: #1976d2;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    `
    
    tag.innerHTML = `
      ${tagText}
      <button type="button" class="tag-remove" style="
        background: none;
        border: none;
        color: #1976d2;
        cursor: pointer;
        padding: 0;
        margin-left: 0.25rem;
        font-size: 1rem;
        line-height: 1;
      ">&times;</button>
    `
    
    // Přidáme tag před input
    container.insertBefore(tag, tagInput)
    
    // Event listener pro odstranění tagu
    const removeBtn = tag.querySelector('.tag-remove')
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      tag.remove()
      this.updateHiddenInput(container, hiddenInput)
    })
    
    this.updateHiddenInput(container, hiddenInput)
  }

  setupTagInputEvents(container, tagInput, hiddenInput, existingTags) {
    // Enter nebo čárka přidá tag
    tagInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        const tagText = tagInput.value.trim()
        if (tagText && !this.tagExists(container, tagText)) {
          this.addTag(container, tagInput, hiddenInput, tagText)
          tagInput.value = ''
        }
      }
      
      // Backspace smaže poslední tag pokud je input prázdný
      if (e.key === 'Backspace' && tagInput.value === '') {
        const tags = container.querySelectorAll('.tag')
        if (tags.length > 0) {
          tags[tags.length - 1].remove()
          this.updateHiddenInput(container, hiddenInput)
        }
      }
    })
    
    // Autocomplete při psaní
    tagInput.addEventListener('input', () => {
      this.showAutocomplete(container, tagInput, existingTags)
    })
    
    // Kliknutí mimo input skryje autocomplete
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        this.hideAutocomplete(container)
      }
    })
  }

  showAutocomplete(container, tagInput, existingTags) {
    const value = tagInput.value.toLowerCase()
    if (!value) {
      this.hideAutocomplete(container)
      return
    }
    
    const matchingTags = existingTags.filter(tag => 
      tag.toLowerCase().includes(value) && !this.tagExists(container, tag)
    )
    
    // Pokud nejsou žádné shodné tagy, ale je zadaný text, zobrazíme možnost vytvořit nový
    if (matchingTags.length === 0 && value.trim()) {
      this.showCreateNewTag(container, tagInput, value.trim())
      return
    }
    
    if (matchingTags.length === 0) {
      this.hideAutocomplete(container)
      return
    }
    
    // Odstraníme starý autocomplete
    this.hideAutocomplete(container)
    
    // Vytvoříme nový autocomplete
    const autocomplete = document.createElement('div')
    autocomplete.className = 'tag-autocomplete'
    autocomplete.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #dee2e6;
      border-top: none;
      border-radius: 0 0 0.375rem 0.375rem;
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    `
    
    matchingTags.forEach(tag => {
      const item = document.createElement('div')
      item.className = 'tag-autocomplete-item'
      item.style.cssText = `
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        border-bottom: 1px solid #f8f9fa;
        transition: background-color 0.15s ease-in-out;
      `
      item.textContent = tag
      
      item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = '#f8f9fa'
      })
      
      item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = 'white'
      })
      
      item.addEventListener('click', () => {
        this.addTag(container, tagInput, this.element, tag)
        tagInput.value = ''
        this.hideAutocomplete(container)
        tagInput.focus()
      })
      
      autocomplete.appendChild(item)
    })
    
    // Přidáme možnost vytvořit nový tag, pokud zadaný text neexistuje
    const exactMatch = existingTags.find(tag => tag.toLowerCase() === value.toLowerCase())
    if (!exactMatch && value.trim()) {
      const newItem = document.createElement('div')
      newItem.className = 'tag-autocomplete-item tag-new'
      newItem.style.cssText = `
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        background-color: #e3f2fd;
        color: #1976d2;
        font-weight: 500;
        border-bottom: 1px solid #f8f9fa;
        transition: background-color 0.15s ease-in-out;
      `
      newItem.innerHTML = `+ Vytvořit "${value}"`
      
      newItem.addEventListener('mouseenter', () => {
        newItem.style.backgroundColor = '#bbdefb'
      })
      
      newItem.addEventListener('mouseleave', () => {
        newItem.style.backgroundColor = '#e3f2fd'
      })
      
      newItem.addEventListener('click', () => {
        this.addTag(container, tagInput, this.element, value)
        tagInput.value = ''
        this.hideAutocomplete(container)
        tagInput.focus()
      })
      
      autocomplete.appendChild(newItem)
    }
    
    container.style.position = 'relative'
    container.appendChild(autocomplete)
  }

  showCreateNewTag(container, tagInput, value) {
    // Odstraníme starý autocomplete
    this.hideAutocomplete(container)
    
    // Vytvoříme autocomplete s možností vytvořit nový tag
    const autocomplete = document.createElement('div')
    autocomplete.className = 'tag-autocomplete'
    autocomplete.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #dee2e6;
      border-top: none;
      border-radius: 0 0 0.375rem 0.375rem;
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    `
    
    const newItem = document.createElement('div')
    newItem.className = 'tag-autocomplete-item tag-new'
    newItem.style.cssText = `
      padding: 0.5rem 0.75rem;
      cursor: pointer;
      background-color: #e3f2fd;
      color: #1976d2;
      font-weight: 500;
      border-bottom: 1px solid #f8f9fa;
      transition: background-color 0.15s ease-in-out;
    `
    newItem.innerHTML = `+ Vytvořit "${value}"`
    
    newItem.addEventListener('mouseenter', () => {
      newItem.style.backgroundColor = '#bbdefb'
    })
    
    newItem.addEventListener('mouseleave', () => {
      newItem.style.backgroundColor = '#e3f2fd'
    })
    
    newItem.addEventListener('click', () => {
      this.addTag(container, tagInput, this.element, value)
      tagInput.value = ''
      this.hideAutocomplete(container)
      tagInput.focus()
    })
    
    autocomplete.appendChild(newItem)
    container.style.position = 'relative'
    container.appendChild(autocomplete)
  }

  hideAutocomplete(container) {
    const autocomplete = container.querySelector('.tag-autocomplete')
    if (autocomplete) {
      autocomplete.remove()
    }
  }

  tagExists(container, tagText) {
    const tags = container.querySelectorAll('.tag')
    return Array.from(tags).some(tag => 
      tag.textContent.trim().replace('×', '').trim() === tagText
    )
  }

  updateHiddenInput(container, hiddenInput) {
    const tags = container.querySelectorAll('.tag')
    const tagTexts = Array.from(tags).map(tag => 
      tag.textContent.trim().replace('×', '').trim()
    )
    hiddenInput.value = tagTexts.join(',')
    
    // Trigger change event pro Rails
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

}
